// Stage 4 — Targeted profile factor multipliers per cell.
//
// Add-ons (cell-level uplift entries) were removed; the only structural
// multiplier the engine applies at this stage is the dimension-driven
// `targetedFactorMultiplier`.

import type { Dimension, DimensionOption, LevelId, Model, ClientProfile, Stage4Cell } from '../types.js';
import { LEVEL_IDS } from '../types.js';
import { exactLevelLabel } from './level-groups.js';

const STRUCTURAL_KINDS = new Set(['structural', 'meta']);

/** Normalize a selection value to a list of option ids. */
export function selectionValues(sel: string | string[] | undefined | null): string[] {
  if (sel === undefined || sel === null || sel === '') return [];
  if (Array.isArray(sel)) return sel.filter((v) => typeof v === 'string' && v !== '');
  return [sel];
}

function optionMultiplier(
  opt: DimensionOption,
  disciplineId: string,
  levelId: LevelId,
): number {
  const tf = opt.targetedFactors;
  if (Array.isArray(tf) && tf.length > 0) {
    let mult = 1;
    for (const entry of tf) {
      const lvOk =
        !entry.levels || entry.levels.length === 0 || entry.levels.includes(levelId);
      const dcOk =
        !entry.disciplines ||
        entry.disciplines.length === 0 ||
        entry.disciplines.includes(disciplineId);
      if (lvOk && dcOk) mult *= Number(entry.factor) || 1;
    }
    return mult;
  }
  if (typeof opt.factor === 'number') return opt.factor;
  return 1;
}

function dimensionMultiplier(
  dim: Dimension,
  sel: string | string[] | undefined,
  disciplineId: string,
  levelId: LevelId,
): number {
  const values = selectionValues(sel);
  if (values.length === 0) return 1;

  let mult = 1;
  for (const value of values) {
    const opt = (dim.options || []).find((o) => o.value === value);
    if (!opt) continue;
    mult *= optionMultiplier(opt, disciplineId, levelId);
  }
  return mult;
}

export function targetedFactorMultiplier(
  model: Model,
  profile: ClientProfile,
  disciplineId: string,
  levelId: LevelId,
): number {
  let mult = 1;
  for (const dim of model.dimensions || []) {
    // Maturity dimensions are applied later (Stage 5), not as cell multipliers.
    if (!STRUCTURAL_KINDS.has(dim.appliesTo)) continue;
    const sel = profile.selections?.[dim.id];
    mult *= dimensionMultiplier(dim, sel, disciplineId, levelId);
  }
  return mult;
}

export function applyStage4(
  model: Model,
  profile: ClientProfile,
  pyramidByDiscipline: Record<string, Partial<Record<LevelId, number>>>,
): Stage4Cell[] {
  const cells: Stage4Cell[] = [];
  for (const disc of model.disciplines || []) {
    const py = pyramidByDiscipline[disc.id] || {};
    for (const lvl of LEVEL_IDS) {
      const base = py[lvl] || 0;
      const tfMult = targetedFactorMultiplier(model, profile, disc.id, lvl);
      const adjusted = base * tfMult;
      cells.push({
        disciplineId: disc.id,
        disciplineLabel: disc.label,
        levelId: lvl,
        levelLabel: exactLevelLabel(lvl),
        baseFtePyramid: base,
        targetedMult: tfMult,
        adjustedFte: adjusted,
      });
    }
  }
  return cells;
}
